import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Share2, Gift, Users, DollarSign, TrendingUp, Twitter, Linkedin, Mail, Loader2 } from "lucide-react";

export default function ReferralsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/referrals/me")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load referrals"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#d4a574]" size={28} /></div>;
  if (!data) return null;

  const copy = (val, label = "Copied") => {
    navigator.clipboard.writeText(val);
    toast.success(`${label}: ${val}`);
  };

  const shareText = `I've been using The Close Out to find distressed CRE deals before they hit the market. Get 10% off your first month → ${data.share_url}`;
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
  const liUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(data.share_url)}`;
  const mailUrl = `mailto:?subject=${encodeURIComponent("Get 10% off The Close Out")}&body=${encodeURIComponent(shareText)}`;

  return (
    <div className="space-y-6" data-testid="referrals-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] mb-2 font-semibold flex items-center gap-2">
          <Gift size={12} /> Give $50 · Get $50
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Refer & Earn.</h1>
        <p className="text-[13px] text-[#b8ac96] mt-2 max-w-2xl">
          Every friend who subscribes with your link gets <span className="text-[#d4a574] font-semibold">10% off their first month</span>. When their first payment clears, you get <span className="text-[#34d399] font-semibold">${data.reward_per_conversion_usd.toFixed(0)} credit</span> on your next invoice.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="kpi-frame rounded-lg p-4" data-testid="stat-signups">
          <div className="flex justify-between items-start mb-2">
            <div className="text-[10px] uppercase tracking-wider text-[#7a6d5a]">Signups</div>
            <Users size={14} className="text-[#d4a574]" />
          </div>
          <div className="font-display text-3xl font-bold text-[#d4a574] font-mono">{data.signups}</div>
        </div>
        <div className="kpi-frame rounded-lg p-4" data-testid="stat-paid">
          <div className="flex justify-between items-start mb-2">
            <div className="text-[10px] uppercase tracking-wider text-[#7a6d5a]">Paid Conversions</div>
            <TrendingUp size={14} className="text-[#e8b988]" />
          </div>
          <div className="font-display text-3xl font-bold text-[#e8b988] font-mono">{data.paid_conversions}</div>
        </div>
        <div className="kpi-frame rounded-lg p-4" data-testid="stat-credits">
          <div className="flex justify-between items-start mb-2">
            <div className="text-[10px] uppercase tracking-wider text-[#7a6d5a]">Credits Earned</div>
            <DollarSign size={14} className="text-[#34d399]" />
          </div>
          <div className="font-display text-3xl font-bold text-[#34d399] font-mono">${data.credits_earned_usd.toFixed(0)}</div>
        </div>
      </div>

      {/* Share card */}
      <div className="border border-[#33291f] rounded-lg bg-gradient-to-br from-[#241d16] to-[#1c1712] p-6">
        <div className="text-[10px] uppercase tracking-wider text-[#7a6d5a] mb-3">Your unique referral link</div>
        <div className="flex gap-2 items-center mb-4">
          <div className="flex-1 px-3 py-2.5 rounded bg-[#14100c] border border-[#33291f] font-mono text-[13px] text-[#d4a574] overflow-x-auto whitespace-nowrap" data-testid="referral-link">
            {data.share_url}
          </div>
          <button onClick={() => copy(data.share_url, "Link copied")} data-testid="copy-link-btn" className="px-4 py-2.5 rounded bg-[#d4a574] text-[#14100c] font-semibold text-[12px] hover:bg-[#e8b988] flex items-center gap-1.5">
            <Copy size={13} /> Copy
          </button>
        </div>

        <div className="text-[10px] uppercase tracking-wider text-[#7a6d5a] mb-2">Or share the code directly</div>
        <div className="flex gap-2 items-center mb-6">
          <div className="px-3 py-2 rounded bg-[#14100c] border border-[#4a3a2b] font-mono text-lg font-bold text-[#d4a574]" data-testid="referral-code">{data.code}</div>
          <button onClick={() => copy(data.code, "Code copied")} data-testid="copy-code-btn" className="px-3 py-2 rounded border border-[#4a3a2b] text-[#d4a574] text-[12px] font-semibold hover:border-[#d4a574]">Copy code</button>
        </div>

        <div className="text-[10px] uppercase tracking-wider text-[#7a6d5a] mb-2">Share on…</div>
        <div className="flex gap-2 flex-wrap">
          <a href={twitterUrl} target="_blank" rel="noreferrer" data-testid="share-twitter" className="flex items-center gap-2 px-4 py-2 rounded border border-[#4a3a2b] text-[12px] text-[#f4ede2] hover:border-[#d4a574] hover:text-[#d4a574]">
            <Twitter size={13} /> Twitter / X
          </a>
          <a href={liUrl} target="_blank" rel="noreferrer" data-testid="share-linkedin" className="flex items-center gap-2 px-4 py-2 rounded border border-[#4a3a2b] text-[12px] text-[#f4ede2] hover:border-[#d4a574] hover:text-[#d4a574]">
            <Linkedin size={13} /> LinkedIn
          </a>
          <a href={mailUrl} data-testid="share-email" className="flex items-center gap-2 px-4 py-2 rounded border border-[#4a3a2b] text-[12px] text-[#f4ede2] hover:border-[#d4a574] hover:text-[#d4a574]">
            <Mail size={13} /> Email
          </a>
        </div>
      </div>

      {/* How it works */}
      <div className="border border-[#33291f] rounded-lg bg-[#1c1712] p-5">
        <div className="font-display text-lg font-semibold mb-4">How it works</div>
        <ol className="space-y-3 text-[13px] text-[#b8ac96]">
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-[#241d16] border border-[#d4a574] flex items-center justify-center text-[11px] font-bold text-[#d4a574] shrink-0">1</span>
            <div>Share your link on LinkedIn, in your email signature, or in CRE Slack/Discord communities.</div>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-[#241d16] border border-[#d4a574] flex items-center justify-center text-[11px] font-bold text-[#d4a574] shrink-0">2</span>
            <div>Your friend signs up. At checkout, code <span className="font-mono text-[#d4a574]">FRIEND50</span> auto-applies 10% off their first month.</div>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-[#241d16] border border-[#d4a574] flex items-center justify-center text-[11px] font-bold text-[#d4a574] shrink-0">3</span>
            <div>When their first paid invoice clears (after the 7-day trial), we credit <span className="text-[#34d399] font-semibold">${data.reward_per_conversion_usd.toFixed(0)}</span> to your Stripe balance — auto-applied to your next invoice.</div>
          </li>
        </ol>
      </div>

      {/* Recent referrals */}
      {data.referrals?.length > 0 && (
        <div className="border border-[#33291f] rounded-lg bg-[#1c1712] overflow-hidden" data-testid="referrals-history">
          <div className="p-4 border-b border-[#33291f] font-display text-lg font-semibold">Recent referrals</div>
          <table className="w-full text-[13px]">
            <thead className="bg-[#241d16] text-[10px] uppercase tracking-wider text-[#7a6d5a]">
              <tr>
                <th className="text-left px-4 py-2">Signed up</th>
                <th className="text-left px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.referrals.map((r, i) => (
                <tr key={i} className="border-t border-[#33291f]">
                  <td className="px-4 py-2 font-mono text-[11px] text-[#b8ac96]">{r.signed_up_at?.slice(0, 10)}</td>
                  <td className="px-4 py-2"><span className="text-[10px] uppercase px-2 py-0.5 rounded bg-[#241d16] border border-[#4a3a2b] text-[#b8ac96]">{r.status || "signed_up"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
